import React, { useEffect, useRef, useState } from 'react';

// Honest, competitor-style trust strip with animated count-up stats.
// NOTE: When you have a real Trustpilot/Google profile + brand/press logos you're
// licensed to show, send them and I'll add those badges here too. Until then we keep
// claims truthful (no invented review counts or company affiliations).
const stats = [
  { to: 1000, suffix: '+', format: true, label: 'AI headshots created' },
  { to: 1, prefix: '~', suffix: ' hr', label: 'Typical delivery' },
  { to: 100, suffix: '%', label: 'Commercial rights' },
  { to: 30, suffix: '-day', label: 'Auto data deletion' },
];

const industries = ['Tech', 'Real Estate', 'Healthcare', 'Legal', 'Finance', 'Consulting'];

const Star = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" style={{ color: '#facc15' }}>
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

function AnimatedCounter({ to, duration = 1400, prefix = '', suffix = '', format = false, start }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!start) return undefined;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVal(to);
      return undefined;
    }

    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, to, duration]);

  const display = format ? val.toLocaleString() : val;
  return <>{prefix}{display}{suffix}</>;
}

const TrustStrip = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="border-y border-white/10 bg-white/[0.02] px-4 py-10">
      <div className="mx-auto max-w-6xl">
        {/* Animated stats */}
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-white">
                <AnimatedCounter to={s.to} prefix={s.prefix} suffix={s.suffix} format={s.format} start={inView} />
              </div>
              <div className="mt-1 text-xs md:text-sm text-white/50">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Rating badge */}
        <div className="mt-8 flex flex-col items-center gap-2">
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3, 4].map((i) => <Star key={i} />)}
          </div>
          <p className="text-sm text-white/70">Loved by 1,000+ professionals</p>
        </div>

        {/* Security / guarantee badges */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs font-medium text-white/55">
          <span className="inline-flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-brand-light">
              <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            SSL-secure checkout
          </span>
          <span className="inline-flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-brand-light">
              <path d="M9 12l2 2 4-4" /><path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7l8-4z" />
            </svg>
            Powered by Stripe
          </span>
          <span className="inline-flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-brand-light">
              <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7l8-4z" />
            </svg>
            30-day money-back guarantee
          </span>
          <span className="inline-flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4 text-brand-light">
              <circle cx="12" cy="12" r="9" /><path d="M9 12l2 2 4-4" />
            </svg>
            We never sell your photos
          </span>
        </div>

        {/* Industries served */}
        <div className="mt-8 flex flex-col items-center gap-3">
          <p className="text-xs uppercase tracking-wider text-white/40">Trusted by professionals across industries</p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {industries.map((name) => (
              <span key={name} className="text-sm font-semibold text-white/40">{name}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustStrip;
